import React, { useEffect, useState, useRef } from 'react';
import './Products.css';
import SwiperCore, { Navigation, Pagination, Autoplay } from 'swiper/core';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDoubleRight } from '@fortawesome/free-solid-svg-icons';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ProductosLoading from '../ProductosLoading/ProductosLoading';
import { Link as Anchor } from "react-router-dom";
import moneda from '../moneda';
import { loadResource } from '../storeData';
import { catalogImageUrl } from '../imageUrl';

SwiperCore.use([Navigation, Pagination, Autoplay]);

const MAS_VENDIDOS_SESSION_KEY = 'catalogoMasVendidosOrden';

const mezclarLista = (lista) => {
    const mezclada = [...lista];

    for (let i = mezclada.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [mezclada[i], mezclada[j]] = [mezclada[j], mezclada[i]];
    }

    return mezclada;
};

const esMasVendido = (producto) => String(producto?.masVendido || '').toLowerCase() === 'si';

export default function Products() {
    const [secciones, setSecciones] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [subcategorias, setSubCategorias] = useState([]);
    const [productos, setProductos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fixedCategories, setFixedCategories] = useState(false);
    const categoriasInputRef = useRef(null);
    const productsListRef = useRef(null);
    const scrollAfterFilterRef = useRef(false);
    const [seccionSeleccionada, setSeccionSeleccionada] = useState('Todo');
    const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('Todo');
    const [subcategoriaSeleccionada, setSubcategoriaSeleccionada] = useState('Todo');
    const [ordenMasVendidos, setOrdenMasVendidos] = useState([]);

    const sameId = (a, b) => String(a ?? '') === String(b ?? '');
    const generalSectionId = 'general';
    const categorySectionId = (categoria) => categoria?.idSeccion || generalSectionId;

    const scheduleFilterScroll = () => {
        scrollAfterFilterRef.current = true;
    };

    const scrollToCatalog = () => {
        const target = productsListRef.current || categoriasInputRef.current;
        if (!target) return;

        const filterHeight = categoriasInputRef.current?.offsetHeight || 76;
        const offset = window.innerWidth <= 1024 ? filterHeight + 12 : 24;
        const targetTop = target.getBoundingClientRect().top + window.pageYOffset - offset;

        window.scrollTo({
            top: Math.max(targetTop, 0),
            behavior: 'smooth',
        });
    };

    const handleClickSeccion = (seccion) => {
        scheduleFilterScroll();
        setSeccionSeleccionada(seccion);
        setCategoriaSeleccionada('Todo');
        setSubcategoriaSeleccionada('Todo');
    };

    const handleClickCategoria = (idCategoria) => {
        scheduleFilterScroll();
        const categoria = categorias.find(item => sameId(item.idCategoria, idCategoria));
        if (categoria) {
            setSeccionSeleccionada(categorySectionId(categoria));
        }
        setCategoriaSeleccionada(idCategoria);
        setSubcategoriaSeleccionada('Todo');
    };

    const handleClickSubcategoria = (subcategoria) => {
        scheduleFilterScroll();
        setSubcategoriaSeleccionada(subcategoria);
    };

    useEffect(() => {
        let active = true;

        Promise.all([
            loadResource('productos'),
            loadResource('secciones'),
            loadResource('categorias'),
            loadResource('subcategorias'),
        ])
            .then(([productosData, seccionesData, categoriasData, subcategoriasData]) => {
                if (!active) return;
                setProductos(productosData || []);
                setSecciones(seccionesData || []);
                setCategorias(categoriasData || []);
                setSubCategorias(subcategoriasData || []);
            })
            .catch(error => {
                console.error('Error al cargar catalogo:', error);
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        window.addEventListener('scroll', handleScroll);
        return () => {
            active = false;
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        const idsMasVendidos = productos
            .filter(esMasVendido)
            .map(producto => String(producto.idProducto));

        if (!idsMasVendidos.length) {
            setOrdenMasVendidos([]);
            return;
        }

        try {
            const ordenGuardado = JSON.parse(sessionStorage.getItem(MAS_VENDIDOS_SESSION_KEY) || '[]');
            const ordenVigente = Array.isArray(ordenGuardado)
                ? ordenGuardado.filter(id => idsMasVendidos.includes(String(id))).map(String)
                : [];
            const idsNuevos = idsMasVendidos.filter(id => !ordenVigente.includes(id));
            const nuevoOrden = [...ordenVigente, ...mezclarLista(idsNuevos)];

            sessionStorage.setItem(MAS_VENDIDOS_SESSION_KEY, JSON.stringify(nuevoOrden));
            setOrdenMasVendidos(nuevoOrden);
        } catch (error) {
            setOrdenMasVendidos(mezclarLista(idsMasVendidos));
        }
    }, [productos]);

    const handleScroll = () => {
        if (categoriasInputRef.current) {
            const bannerElement = document.querySelector('.BannerContain');
            const bannerHeight = bannerElement ? bannerElement.offsetHeight : 0;

            if (window.scrollY > categoriasInputRef.current.offsetTop && window.scrollY > bannerHeight) {
                setFixedCategories(true);
            } else {
                setFixedCategories(false);
            }
        }
    };

    useEffect(() => {
        if (!scrollAfterFilterRef.current || loading) return;

        scrollAfterFilterRef.current = false;
        const scrollTimer = window.setTimeout(scrollToCatalog, 60);

        return () => window.clearTimeout(scrollTimer);
    }, [seccionSeleccionada, categoriaSeleccionada, subcategoriaSeleccionada, loading]);

    const obtenerImagen = (item) => {
        if (item.imagen1) {
            return item.imagen1;
        } else if (item.imagen2) {
            return item.imagen2;
        } else if (item.imagen3) {
            return item.imagen3;
        } else if (item.imagen4) {
            return item.imagen4;
        }
        return null;
    };

    const categoriasConProductos = categorias?.filter(categoria =>
        productos?.some(producto => sameId(producto?.idCategoria, categoria?.idCategoria))
    );

    const seccionesConFallback = [
        ...secciones,
        ...(categoriasConProductos.some(categoria => !categoria.idSeccion) ? [{ idSeccion: generalSectionId, seccion: 'General', orden: 999 }] : [])
    ]?.filter((seccion, index, arr) =>
        arr.findIndex(item => sameId(item.idSeccion, seccion.idSeccion)) === index &&
        categoriasConProductos?.some(categoria => sameId(categorySectionId(categoria), seccion.idSeccion))
    );

    const categoriasPorSeccion = (idSeccion) => categoriasConProductos
        ?.filter(categoria => sameId(categorySectionId(categoria), idSeccion))
        ?.sort((a, b) => Number(a.orden || 0) - Number(b.orden || 0));

    const categoriasVisibles = seccionSeleccionada === 'Todo'
        ? categoriasConProductos
        : categoriasPorSeccion(seccionSeleccionada);

    const subcategoriasVisibles = subcategorias
        ?.filter(subcategoria =>
            sameId(subcategoria.idCategoria, categoriaSeleccionada) &&
            productos.some(producto => sameId(producto.idSubCategoria, subcategoria.idSubCategoria))
        );

    const productosDeCategoria = (idCategoria) => productos?.filter(item => sameId(item.idCategoria, idCategoria));

    const productosSeleccionados = productos?.filter(item => {
        if (categoriaSeleccionada === 'Todo') {
            return false;
        }

        if (subcategoriaSeleccionada === 'Todo' || !subcategoriaSeleccionada) {
            return sameId(item.idCategoria, categoriaSeleccionada);
        }

        return sameId(item.idSubCategoria, subcategoriaSeleccionada);
    });

    const productosMasVendidos = productos
        ?.filter(esMasVendido)
        ?.sort((a, b) => {
            const indexA = ordenMasVendidos.indexOf(String(a.idProducto));
            const indexB = ordenMasVendidos.indexOf(String(b.idProducto));

            if (indexA === -1 && indexB === -1) return 0;
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });

    const productUrl = (item) => `/producto/${item.idProducto}/${item.titulo?.replace(/\s+/g, '-') || 'producto'}`;

    const renderPrice = (item) => (
        <div className='deFLexPrice'>
            <h5> {moneda} {String(item?.precio)?.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</h5>
            {(item.precioAnterior >= 1 && item.precioAnterior !== undefined) && (
                <h5 className='precioTachado'>{moneda} {`${item?.precioAnterior}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</h5>
            )}
        </div>
    );

    const renderMasVendidoCard = (item) => (
        <SwiperSlide key={item.idProducto} id='SwiperSlide-scroll-products-masvendidos'>
            <Anchor className='cardProdcutmasVendido' to={productUrl(item)}>
                <img src={catalogImageUrl(obtenerImagen(item))} alt="imagen" loading="lazy" decoding="async" />
                <h6 className='masVendido'>Mas Vendido</h6>
                <div className='cardText'>
                    <h4>{item.titulo}</h4>
                    <span>{item.descripcion}</span>
                    {renderPrice(item)}
                </div>
            </Anchor>
        </SwiperSlide>
    );

    const renderProductSlide = (item) => (
        <SwiperSlide id='SwiperSlide-scroll-products' key={item.idProducto}>
            <Anchor className='cardProdcut' to={productUrl(item)}>
                <img src={catalogImageUrl(obtenerImagen(item))} alt="imagen" loading="lazy" decoding="async" />
                <div className='cardText'>
                    <h4>{item.titulo}</h4>
                    <span>{item.descripcion}</span>
                    {renderPrice(item)}
                    <FontAwesomeIcon icon={faAngleDoubleRight} className='iconCard' />
                </div>
            </Anchor>
        </SwiperSlide>
    );

    const renderCategoriaSection = (categoria) => {
        const productosCategoria = productosDeCategoria(categoria.idCategoria);

        if (!productosCategoria?.length) {
            return null;
        }

        return (
            <div key={categoria.idCategoria} className='categoriSection'>
                <div className='deFlexTitlesection'>
                    <div>
                        <span>{categoria.seccion || 'General'}</span>
                        <h3>{categoria.categoria}</h3>
                    </div>
                    <button onClick={() => handleClickCategoria(categoria.idCategoria)}>
                        Ver mas
                    </button>
                </div>

                <Swiper
                    effect={'coverflow'}
                    grabCursor={true}
                    slidesPerView={'auto'}
                    id='swiper_container_products'
                >
                    {productosCategoria?.map(renderProductSlide)}
                </Swiper>
            </div>
        );
    };

    return (
        <div className='ProductsContain'>
            <ToastContainer />
            {productos?.length > 0 && (
                <div className={`categoriasInputs ${fixedCategories ? 'fixed' : ''}`} id='FlexIn' ref={categoriasInputRef}>
                    <div className='categoriasInputsFilter'>
                        <input
                            type="button"
                            value="Todo"
                            onClick={() => handleClickSeccion('Todo')}
                            style={{
                                background: seccionSeleccionada === 'Todo' ? 'var(--brand-gradient)' : '',
                                color: seccionSeleccionada === 'Todo' ? 'var(--blanco)' : '',
                            }}
                        />
                        {seccionesConFallback?.map(({ seccion, idSeccion }) => (
                            <div className='columnInput' key={idSeccion}>
                                <input
                                    type="button"
                                    value={seccion}
                                    onClick={() => handleClickSeccion(idSeccion)}
                                    style={{
                                        background: sameId(seccionSeleccionada, idSeccion) ? 'var(--brand-gradient)' : '',
                                        color: sameId(seccionSeleccionada, idSeccion) ? 'var(--blanco)' : '',
                                    }}
                                />
                            </div>
                        ))}
                    </div>

                    {seccionSeleccionada !== 'Todo' && categoriasVisibles?.length > 0 && (
                        <div className='categoriasInputsFilter' id='categoriaInputs'>
                            <input
                                type="button"
                                value="Todo"
                                onClick={() => handleClickCategoria('Todo')}
                                style={{
                                    borderBottom: categoriaSeleccionada === 'Todo' ? '2px solid var(--color2)' : '',
                                    color: categoriaSeleccionada === 'Todo' ? 'var(--color1)' : '',
                                }}
                                id='subcategoriaInput'
                            />
                            {categoriasVisibles?.map(categoria => (
                                <div key={categoria.idCategoria}>
                                    <input
                                        type="button"
                                        value={categoria.categoria}
                                        onClick={() => handleClickCategoria(categoria.idCategoria)}
                                        style={{
                                            borderBottom: sameId(categoriaSeleccionada, categoria.idCategoria) ? '2px solid var(--color2)' : '',
                                            color: sameId(categoriaSeleccionada, categoria.idCategoria) ? 'var(--color1)' : '',
                                        }}
                                        id='subcategoriaInput'
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {categoriaSeleccionada !== 'Todo' && subcategoriasVisibles?.length > 0 && (
                        <div className='categoriasInputsFilter' id='subcategoriasInputs'>
                            <input
                                type="button"
                                value="Todo"
                                onClick={() => handleClickSubcategoria('Todo')}
                                style={{
                                    borderBottom: subcategoriaSeleccionada === 'Todo' ? '2px solid var(--color2)' : '',
                                    color: subcategoriaSeleccionada === 'Todo' ? 'var(--color1)' : '',
                                }}
                                id='subcategoriaInput'
                            />

                            {subcategoriasVisibles?.map(subcategoria => (
                                <div key={subcategoria.idSubCategoria}>
                                    <input
                                        type="button"
                                        value={subcategoria.subcategoria}
                                        onClick={() => handleClickSubcategoria(subcategoria.idSubCategoria)}
                                        style={{
                                            borderBottom: sameId(subcategoriaSeleccionada, subcategoria.idSubCategoria) ? '2px solid var(--color2)' : '',
                                            color: sameId(subcategoriaSeleccionada, subcategoria.idSubCategoria) ? 'var(--color1)' : '',
                                        }}
                                        id='subcategoriaInput'
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {loading ? (
                <ProductosLoading />
            ) : (
                <div className='Products' ref={productsListRef}>
                    {categoriaSeleccionada === 'Todo' && (
                        <>
                            {productosMasVendidos?.length > 0 && (
                                <div className='categoriSection'>
                                    <Swiper
                                        effect={'coverflow'}
                                        grabCursor={true}
                                        slidesPerView={'auto'}
                                        id='swiper_container_products'
                                        autoplay={{ delay: 3000 }}
                                    >
                                        {productosMasVendidos?.slice(0, 20)?.map(renderMasVendidoCard)}
                                    </Swiper>
                                </div>
                            )}

                            {seccionSeleccionada === 'Todo' ? (
                                seccionesConFallback?.map(itemSeccion => (
                                    <div key={itemSeccion.idSeccion} className='seccionCatalogo'>
                                        <div className='deFlexTitleSeccion'>
                                            <h2>{itemSeccion.seccion}</h2>
                                            <button onClick={() => handleClickSeccion(itemSeccion.idSeccion)}>
                                                Ver seccion
                                            </button>
                                        </div>
                                        {categoriasPorSeccion(itemSeccion.idSeccion)?.map(renderCategoriaSection)}
                                    </div>
                                ))
                            ) : (
                                categoriasVisibles?.map(renderCategoriaSection)
                            )}
                        </>
                    )}

                    {categoriaSeleccionada !== 'Todo' && (
                        <div className='categoriSectionSelected'>
                            {productosSeleccionados?.map(item => (
                                <Anchor key={item.idProducto} to={productUrl(item)}>
                                    <div className='cardProdcutSelected'>
                                        <img src={catalogImageUrl(obtenerImagen(item))} alt="imagen" loading="lazy" decoding="async" />
                                        <div className='cardTextSelected'>
                                            <h4>{item.titulo}</h4>
                                            <span>{item.descripcion}</span>
                                            {renderPrice(item)}
                                            <FontAwesomeIcon icon={faAngleDoubleRight} className='iconCard' />
                                        </div>
                                    </div>
                                </Anchor>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
